import { BOOLEAN_SELECT_LIST_ITEMS, Grid, pn, useElementBreakpoints } from "@compass/core";
import { DatePicker, Select } from "components";
import {
  SurveyResponseFilter as SurveyResponseFilterModel,
  SurveyResponseFilterResource
} from "generated/openApi";
import produce from "immer";
import { useEffect, useState } from "react";
import ApiService from "services/apiService";

export const SurveyResponseFilter = (props: {
  filter: SurveyResponseFilterModel;
  setFilter: (value: SurveyResponseFilterModel) => void;
}) => {
  const { breakpointsRef, smallFieldBreakpoints, largeFieldBreakpoints } = useElementBreakpoints();
  const [resource, setResource] = useState<SurveyResponseFilterResource>({});
  const properties = resource.properties || [];

  useEffect(() => {
    ApiService.surveyResponses.getFilterResource().then((response) => {
      setResource(response);
    });
  }, []);

  const isCompleteField = () => {
    const propertyName = pn<SurveyResponseFilterModel>("isComplete");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.filter.isComplete)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft!.isComplete = value?.id;
            })
          )
        }
      />
    );
  };

  const surveysField = () => {
    const propertyName = pn<SurveyResponseFilterModel>("surveyIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={resource.surveys?.filter((x) => props.filter.surveyIds?.includes(x.id))}
        options={resource.surveys}
        getOptionLabel={option => option.name}
        onChange={(e, values) => {
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.surveyIds = values.map((x) => x.id);
            })
          );
        }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const surveyTypesField = () => {
    const propertyName = pn<SurveyResponseFilterModel>("surveyTypeIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={resource.surveyTypes?.filter((x) => props.filter.surveyTypeIds?.includes(x.id))}
        options={resource.surveyTypes}
        getOptionLabel={option => option.name}
        onChange={(e, values) => {
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.surveyTypeIds = values.map((x) => x.id);
            })
          );
        }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const startDateField = () => {
    const propertyName = pn<SurveyResponseFilterModel>("startDate");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DatePicker
        modelProperty={property}
        value={props.filter.startDate}
        onChange={(value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.startDate = value;
            })
          )
        }
      />
    );
  };

  const endDateField = () => {
    const propertyName = pn<SurveyResponseFilterModel>("endDate");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DatePicker
        modelProperty={property}
        value={props.filter.endDate}
        onChange={(value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.endDate = value;
            })
          )
        }
      />
    );
  };

  return (
    <Grid ref={breakpointsRef} container spacing="sm">
      <Grid item xs={smallFieldBreakpoints}>
        {isCompleteField()}
      </Grid>
      <Grid item xs={smallFieldBreakpoints}>
        {startDateField()}
      </Grid>
      <Grid item xs={smallFieldBreakpoints}>
        {endDateField()}
      </Grid>
      <Grid item xs={largeFieldBreakpoints}>
        {surveysField()}
      </Grid>
      <Grid item xs={largeFieldBreakpoints}>
        {surveyTypesField()}
      </Grid>
    </Grid>
  );
};
